/**
 * Componente de FAQ editable.
 *
 * Vive fuera del editor porque un node atómico con attrs se edita mejor con
 * inputs normales que con comandos de TipTap: el autor escribe la pregunta en
 * un input y la respuesta en un textarea, sin pelearse con el cursor.
 */

import { useState } from "react";
import type { FaqEntry } from "@vimos/rich-content";
import { collapseWhitespace } from "@vimos/rich-content";

interface FaqEditorProps {
  items: FaqEntry[];
  /** Texto plano del cuerpo, para proponer preguntas ya redactadas. */
  sourceText?: string;
  onChange: (items: FaqEntry[]) => void;
}

export function FaqEditor({ items, sourceText = "", onChange }: FaqEditorProps) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  function add() {
    const q = question.trim();
    const a = answer.trim();
    if (!q || !a) return;
    // Si el texto trae preguntas ya escritas, se ofrecen como candidato en
    // lugar de obligar a reescribirlas.
    onChange([...items, { question: q, answer: a }]);
    setQuestion("");
    setAnswer("");
    setOpen(false);
  }

  function update(index: number, patch: Partial<FaqEntry>) {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function remove(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    onChange(next);
  }

  /**
   * Propone preguntas a partir del propio artículo: busca líneas cortas en
   * forma de pregunta o encabezados H2/H3, que es donde el autor ya dejó la
   * estructura mental aunque todavía no la marcó como FAQ.
   */
  function suggestFromContent(): void {
    const lines = sourceText
      .split(/\n+/)
      .map((line) => collapseWhitespace(line))
      .filter(Boolean);

    const existing = new Set(items.map((item) => item.question.toLowerCase().trim()));

    const candidates = lines.filter((line) => {
      if (line.length < 12 || line.length > 140) return false;
      const isQuestion = /[?¿]\s*$/.test(line);
      const isHeading = /^#{2,3}\s+/.test(line);
      if (!isQuestion && !isHeading) return false;
      const cleaned = line
        .replace(/^#{2,3}\s+/, "")
        .replace(/[?¿]\s*$/, "")
        .trim();
      return cleaned.length > 8 && !existing.has(cleaned.toLowerCase());
    });

    const suggested = candidates
      .slice(0, 6)
      .map((line) => {
        const question = line
          .replace(/^#{2,3}\s+/, "")
          .replace(/\s*[?¿]\s*$/, "")
          .trim();
        return { question, answer: "" };
      })
      .filter((entry) => entry.question.length > 0);

    // Siempre se deja al menos un par vacío: el autor tiene que poder escribir
    // la respuesta, y un array vacío no deja sitio donde escribirla.
    onChange([...items, ...suggested, ...(suggested.length > 0 ? [] : [{ question: "", answer: "" }])]);
  }

  return (
    <div className="border-sand/40 mt-5 rounded-md border bg-[#f7f7f4] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-body text-ink text-sm font-semibold">Preguntas frecuentes</p>
          <p className="text-mist mt-0.5 text-xs">
            Se muestran como acordeón visible. Si hay preguntas, la página emite el schema FAQPage.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={suggestFromContent}
            className="border-navy/15 text-navy hover:bg-navy rounded-full border px-3 py-1.5 text-xs font-semibold hover:text-white"
          >
            Sugerir
          </button>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="bg-navy hover:bg-navy-700 rounded-full px-3 py-1.5 text-xs font-semibold text-white"
          >
            {open ? "Cancelar" : "Agregar"}
          </button>
        </div>
      </div>

      {items.length > 0 && (
        <ul className="mt-4 space-y-3">
          {items.map((item, index) => (
            <li key={index} className="border-sand/50 bg-paper rounded-md border p-3">
              <input
                value={item.question}
                onChange={(event) => update(index, { question: event.target.value })}
                placeholder="Pregunta"
                className="border-sand/70 text-ink focus-visible:ring-gold h-10 w-full rounded-md border px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2"
              />
              <textarea
                value={item.answer}
                onChange={(event) => update(index, { answer: event.target.value })}
                placeholder="Respuesta"
                rows={3}
                className="border-sand/70 text-ink focus-visible:ring-gold mt-2 w-full rounded-md border px-3 py-2 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2"
              />
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label="Subir pregunta"
                  className="border-sand/70 text-ink rounded-full border px-2.5 py-1 text-xs disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  aria-label="Bajar pregunta"
                  className="border-sand/70 text-ink rounded-full border px-2.5 py-1 text-xs disabled:opacity-40"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="ml-auto rounded-full border border-red-200 px-2.5 py-1 text-xs text-red-600 hover:bg-red-50"
                >
                  Quitar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="border-sand/50 bg-paper mt-4 rounded-md border p-3">
          <input
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Pregunta"
            className="border-sand/70 text-ink focus-visible:ring-gold h-10 w-full rounded-md border px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2"
          />
          <textarea
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder="Respuesta"
            rows={3}
            className="border-sand/70 text-ink focus-visible:ring-gold mt-2 w-full rounded-md border px-3 py-2 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2"
          />
          <button
            type="button"
            onClick={add}
            disabled={!question.trim() || !answer.trim()}
            className="bg-gold text-navy mt-2 rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40"
          >
            Agregar pregunta
          </button>
        </div>
      )}
    </div>
  );
}
